import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container, Row, Col, Button, Card, CardBody } from "shards-react";
import { CircularProgress, Collapse, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import api from "../../api";
import PageTitle from "../../PageTitle";

const unwrap = data => {
  if (data && Object.prototype.hasOwnProperty.call(data, "data")) {
    return data.data;
  }
  return data;
};

const statusLabel = value => {
  if (value === "draft") return "Borrador";
  if (value === "published") return "Publicada";
  if (value === "archived") return "Archivada";
  return value || "-";
};

const formatDate = value => {
  if (!value) return "";
  const date = String(value).slice(0, 10);
  const parts = date.split("-");
  if (parts.length !== 3) return value;
  return parts[2] + "/" + parts[1] + "/" + parts[0];
};

const formatMoney = value => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return (
    "$ " +
    number.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  );
};

const formatKm = value => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return number.toLocaleString("es-AR");
};

const versionLabel = version => {
  if (!version) return "";
  if (version.full_name) return version.full_name;
  const variation = version.variation || {};
  const brand = variation.brand ? variation.brand.name : "";
  return [brand, variation.model_description, version.name]
    .filter(Boolean)
    .join(" ");
};

const vehicleColor = vehicle => {
  if (!vehicle || vehicle.color == null || vehicle.color === "") return "—";
  if (typeof vehicle.color === "string") return vehicle.color;
  if (vehicle.color.name) return vehicle.color.name;
  return "—";
};

const countStock = stock => {
  const vehicles = stock || [];
  const used = vehicles.filter(vehicle => vehicle.is_used).length;
  return {
    total: vehicles.length,
    used: used,
    fresh: vehicles.length - used
  };
};

const Stat = ({ label, value, hint }) => (
  <Col md="3" sm="6" className="mb-3">
    <Card className="h-100 mb-0">
      <CardBody className="py-3">
        <div style={{ fontSize: "0.7rem", color: "#6c757d" }}>{label}</div>
        <div style={{ fontSize: "1.35rem", fontWeight: 600, lineHeight: 1.2 }}>
          {value}
        </div>
        {hint ? (
          <div style={{ fontSize: "0.7rem", color: "#6c757d" }}>{hint}</div>
        ) : null}
      </CardBody>
    </Card>
  </Col>
);

const PriceStat = ({ label, value, hint }) => (
  <Col md="3" sm="6" className="mb-2">
    <div style={{ fontSize: "0.7rem", color: "#6c757d" }}>{label}</div>
    <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>{value}</div>
    {hint ? (
      <div style={{ fontSize: "0.7rem", color: "#6c757d" }}>{hint}</div>
    ) : null}
  </Col>
);

const ItemStock = ({ item }) => {
  const stock = item.stock || [];
  const counts = countStock(stock);
  const [open, setOpen] = useState(false);
  const versions =
    (item.versions || [])
      .map(link => versionLabel(link.version) || link.version_id)
      .filter(Boolean)
      .join(", ") || "Sin versiones";

  return (
    <Card className="mb-3">
      <CardBody>
        <button
          type="button"
          onClick={() => {
            if (counts.total > 0) setOpen(!open);
          }}
          aria-expanded={open}
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            background: "transparent",
            border: 0,
            padding: 0,
            textAlign: "left",
            cursor: counts.total > 0 ? "pointer" : "default"
          }}
        >
          <div>
            <Typography style={{ fontSize: "0.95rem", fontWeight: 600 }}>
              {item.label || "Ítem"}
            </Typography>
            <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>
              {counts.total}{" "}
              {counts.total === 1 ? "unidad" : "unidades"}
              {" · "}
              {counts.fresh} {counts.fresh === 1 ? "nuevo" : "nuevos"}
              {" · "}
              {counts.used} {counts.used === 1 ? "usado" : "usados"}
            </div>
          </div>
          {counts.total > 0 && (
            <ExpandMoreIcon
              style={{
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 150ms ease"
              }}
            />
          )}
        </button>
        <div style={{ fontSize: "0.75rem", color: "#6c757d", marginTop: 8 }}>
          {versions}
        </div>
        <Row className="mt-3">
          <PriceStat
            label="Km incluidos / mes"
            value={formatKm(item.km_included_monthly)}
          />
          <PriceStat
            label="Km extra"
            value={formatMoney(item.km_excess_price)}
          />
          <PriceStat
            label="0 km"
            value={formatMoney(item.price_new_monthly)}
            hint={"Mín. " + formatMoney(item.price_new_min)}
          />
          <PriceStat
            label="Usado"
            value={formatMoney(item.price_used_monthly)}
            hint={"Mín. " + formatMoney(item.price_used_min)}
          />
        </Row>
        {counts.total === 0 ? (
          <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>
            Sin unidades
          </div>
        ) : (
          <Collapse in={open}>
            <div className="table-responsive mt-2">
              <table
                className="table table-sm mb-0"
                style={{ fontSize: "0.75rem" }}
              >
                <thead>
                  <tr>
                    <th>Patente</th>
                    <th>Año</th>
                    <th>Km</th>
                    <th>Condición</th>
                    <th>Color</th>
                    <th>Marca</th>
                    <th>Modelo</th>
                    <th>Versión</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.map(vehicle => {
                    const version = vehicle.version || {};
                    const variation = version.variation || {};
                    const brand = variation.brand || {};
                    return (
                      <tr key={vehicle.id}>
                        <td>{vehicle.plate}</td>
                        <td>{vehicle.year}</td>
                        <td>{formatKm(vehicle.last_km)}</td>
                        <td>{vehicle.is_used ? "Usado" : "Nuevo"}</td>
                        <td>{vehicleColor(vehicle)}</td>
                        <td>{brand.name || ""}</td>
                        <td>{variation.model_description || ""}</td>
                        <td>{version.name || ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Collapse>
        )}
      </CardBody>
    </Card>
  );
};

const FlexPriceListStock = ({ match }) => {
  const id = match && match.params ? match.params.id : null;
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    axios
      .get(api.flexPriceLists().stock(id), {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setList(unwrap(res.data));
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        setError("No se pudo cargar el stock de la lista");
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <Container fluid className="main-content-container px-4 pb-4">
        <CircularProgress className="mt-4" />
      </Container>
    );
  }

  const items = list && list.items ? list.items : [];
  const totals = items.reduce(
    (acc, item) => {
      const counts = countStock(item.stock);
      acc.total += counts.total;
      acc.fresh += counts.fresh;
      acc.used += counts.used;
      return acc;
    },
    { total: 0, fresh: 0, used: 0 }
  );
  const totalUnits =
    list && list.stock_count != null ? list.stock_count : totals.total;

  return (
    <Container fluid className="main-content-container px-4 pb-4">
      <Row noGutters className="page-header pt-4 align-items-center">
        <PageTitle
          lg="8"
          title={list ? list.name : "Stock de lista de precios Flex"}
          subtitle="Stock"
          className="text-lg-left"
        />
        <Col lg="4" className="text-lg-right pb-2">
          <Button
            outline
            theme="secondary"
            onClick={() =>
              (window.location.href = "/bookings/flex-price-lists")
            }
          >
            Volver
          </Button>
        </Col>
      </Row>
      {error || !list ? (
        <div className="mt-2" style={{ color: "red" }}>
          {error || "No se pudo cargar el stock de la lista"}
        </div>
      ) : (
        <React.Fragment>
          <Row className="mt-3">
            <Stat label="Unidades" value={totalUnits} />
            <Stat label="Nuevos" value={totals.fresh} />
            <Stat label="Usados" value={totals.used} />
            <Stat
              label="Ítems"
              value={items.length}
              hint={
                "Año máximo usado: " +
                (list.used_max_year != null ? list.used_max_year : "-")
              }
            />
          </Row>
          <div style={{ fontSize: "0.8rem", color: "#5a6169" }} className="mb-3">
            {statusLabel(list.status)}
            {" · "}
            {formatDate(list.valid_from) || "Sin inicio"}
            {" — "}
            {formatDate(list.valid_to) || "Sin fin"}
          </div>
          {items.length === 0 ? (
            <Card>
              <CardBody style={{ color: "#6c757d" }}>
                Esta lista no tiene ítems.
              </CardBody>
            </Card>
          ) : (
            items.map(item => <ItemStock key={item.id} item={item} />)
          )}
        </React.Fragment>
      )}
    </Container>
  );
};

export default FlexPriceListStock;
